import React, { useEffect, useState } from 'react';
import {
  Table, Card, Switch, message, Tag, Space, Typography, Button, Modal, Form, Input, Tabs,
} from 'antd';
import { PlusOutlined, DeleteOutlined, CheckOutlined, CloseOutlined, SyncOutlined } from '@ant-design/icons';
import {
  getUsers, getPendingUsers, getWhitelistGroups, updateUserRole, updateUserSuperAdmin, register, deleteUser, approveUser, rejectUser, syncStaff,
} from '../services/api';
import { useAuth } from '../context/AuthContext';

const { Title, Text } = Typography;

export default function StaffList() {
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [whitelistGroups, setWhitelistGroups] = useState([]);
  const [whitelistTotal, setWhitelistTotal] = useState(0);
  const [whitelistLoading, setWhitelistLoading] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [form] = Form.useForm();
  const { user: currentUser } = useAuth();
  const isSuperAdmin = !!currentUser?.is_super_admin;

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await getUsers();
      if (res.data.success) {
        setUsers(res.data.data || []);
      }
    } catch (err) {
      const msg = err.response?.data?.message || '获取用户列表失败';
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingUsers = async () => {
    setPendingLoading(true);
    try {
      const res = await getPendingUsers();
      if (res.data.success) {
        setPendingUsers(res.data.data || []);
      }
    } catch (err) {
      const msg = err.response?.data?.message || '获取待审批列表失败';
      message.error(msg);
    } finally {
      setPendingLoading(false);
    }
  };

  const fetchWhitelistGroups = async () => {
    setWhitelistLoading(true);
    try {
      const res = await getWhitelistGroups();
      if (res.data.success) {
        const payload = res.data.data || {};
        setWhitelistGroups(payload.groups || []);
        setWhitelistTotal(payload.total || 0);
      }
    } catch (err) {
      const msg = err.response?.data?.message || '获取白名单分组失败';
      message.error(msg);
    } finally {
      setWhitelistLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchPendingUsers();
    fetchWhitelistGroups();
  }, []);

  const handleRoleChange = async (id, checked) => {
    const role = checked ? 'admin' : 'user';
    try {
      const res = await updateUserRole(id, { role });
      if (res.data.success) {
        message.success('角色更新成功');
        setUsers((prev) =>
          prev.map((u) => (u.id === id ? { ...u, role } : u))
        );
      }
    } catch (err) {
      const msg = err.response?.data?.message || '更新角色失败';
      message.error(msg);
    }
  };

  const handleSuperAdminChange = async (id, checked) => {
    try {
      const res = await updateUserSuperAdmin(id, { is_super_admin: checked });
      if (res.data.success) {
        message.success(res.data.message);
        setUsers((prev) =>
          prev.map((u) => (u.id === id ? { ...u, is_super_admin: checked } : u))
        );
      }
    } catch (err) {
      const msg = err.response?.data?.message || '更新超级管理员权限失败';
      message.error(msg);
    }
  };

  const handleSyncStaff = async () => {
    setSyncLoading(true);
    try {
      const res = await syncStaff();
      if (res.data.success) {
        message.success(`同步完成，共 ${res.data.data?.total || 0} 人`);
        fetchWhitelistGroups();
      }
    } catch (err) {
      message.error(err.response?.data?.message || '同步失败');
    } finally {
      setSyncLoading(false);
    }
  };

  const handleAddUser = async (values) => {
    setAddLoading(true);
    try {
      const res = await register({
        email: values.email,
        password: values.password,
        name: values.name,
        adminCreate: true,
      });
      if (res.data.success) {
        message.success('用户添加成功');
        setAddModalOpen(false);
        form.resetFields();
        fetchUsers();
      }
    } catch (err) {
      const msg = err.response?.data?.message || '添加用户失败';
      message.error(msg);
    } finally {
      setAddLoading(false);
    }
  };

  const handleApprove = async (record) => {
    try {
      const res = await approveUser(record.id);
      if (res.data.success) {
        message.success('审批通过');
        fetchPendingUsers();
        fetchUsers();
      }
    } catch (err) {
      message.error(err.response?.data?.message || '审批失败');
    }
  };

  const handleReject = async (record) => {
    Modal.confirm({
      title: `确认拒绝 ${record.name} 的注册申请？`,
      content: `此操作将删除 ${record.email} 的申请记录，不可恢复`,
      okText: '确认拒绝',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const res = await rejectUser(record.id);
          if (res.data.success) {
            message.success('已拒绝');
            fetchPendingUsers();
          }
        } catch (err) {
          message.error(err.response?.data?.message || '操作失败');
        }
      },
    });
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: `确认删除用户 ${record.name}？`,
      content: `此操作将永久删除 ${record.email}，不可恢复`,
      okText: '确认删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const res = await deleteUser(record.id);
          if (res.data.success) {
            message.success('删除成功');
            fetchUsers();
          }
        } catch (err) {
          const msg = err.response?.data?.message || '删除失败';
          message.error(msg);
        }
      },
    });
  };

  const userColumns = [
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      render: (role, record) => {
        if (record.is_super_admin) return <Tag color="red">超级管理员</Tag>;
        if (role === 'admin') return <Tag color="red">管理员</Tag>;
        return <Tag color="default">普通用户</Tag>;
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        if (status === 'active') return <Tag color="success">正常</Tag>;
        if (status === 'pending') return <Tag color="warning">待审批</Tag>;
        return <Tag>{status}</Tag>;
      },
    },
    {
      title: '注册时间',
      dataIndex: 'created_at',
      key: 'created_at',
    },
    {
      title: '最后登录',
      dataIndex: 'last_login_at',
      key: 'last_login_at',
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => {
        if (!isSuperAdmin) return '-';
        const isSelf = currentUser?.id === record.id;
        return (
          <Space direction="vertical" size={4} style={{ alignItems: 'flex-start' }}>
            <Space>
              <span>管理员</span>
              <Switch
                checked={record.role === 'admin'}
                disabled={isSelf}
                onChange={(checked) => handleRoleChange(record.id, checked)}
              />
            </Space>
            <Space>
              <span>超级管理员</span>
              <Switch
                checked={record.is_super_admin}
                disabled={isSelf}
                onChange={(checked) => handleSuperAdminChange(record.id, checked)}
              />
            </Space>
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              disabled={isSelf}
              onClick={() => handleDelete(record)}
              style={{ padding: 0 }}
            >
              删除
            </Button>
          </Space>
        );
      },
    },
  ];

  const pendingColumns = [
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: '申请时间',
      dataIndex: 'created_at',
      key: 'created_at',
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<CheckOutlined />}
            onClick={() => handleApprove(record)}
          >
            通过
          </Button>
          <Button
            danger
            size="small"
            icon={<CloseOutlined />}
            onClick={() => handleReject(record)}
          >
            拒绝
          </Button>
        </Space>
      ),
    },
  ];

  const whitelistUserColumns = [
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => name || <Text type="secondary">{record.email}</Text>,
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: '来源',
      dataIndex: 'source',
      key: 'source',
      width: 110,
      render: (source) => (
        <Tag color={source === 'wecom' ? 'green' : 'blue'}>
          {source === 'wecom' ? '企业微信' : '手动'}
        </Tag>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status) => (
        <Tag color={status === 'active' ? 'success' : 'default'}>
          {status === 'active' ? '有效' : status}
        </Tag>
      ),
    },
    {
      title: '更新时间',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 180,
    },
  ];

  const whitelistGroupColumns = [
    {
      title: '分组',
      dataIndex: 'department',
      key: 'department',
      render: (department, record) => (
        <Space>
          <Tag color={department === '未分组' ? 'default' : 'blue'}>{department}</Tag>
          <Text type="secondary">{record.count} 人</Text>
        </Space>
      ),
    },
    {
      title: '成员预览',
      dataIndex: 'users',
      key: 'users',
      render: (users = []) => (
        <Space wrap size={[4, 4]}>
          {users.slice(0, 8).map((user) => (
            <Tag key={`${user.id}-${user.email}`}>{user.name || user.email}</Tag>
          ))}
          {users.length > 8 && <Tag>+{users.length - 8}</Tag>}
        </Space>
      ),
    },
  ];

  return (
    <div className="page-shell">
      <div className="page-heading">
        <div>
          <div className="page-kicker">Access Control</div>
          <Title level={3} className="page-heading-title">用户管理</Title>
          <Text className="page-heading-subtitle">管理员工账号、审批注册申请和同步企业微信人员信息。</Text>
        </div>
        {isSuperAdmin && (
          <Space wrap>
            <Button icon={<SyncOutlined spin={syncLoading} />} loading={syncLoading} onClick={handleSyncStaff}>
              同步企业微信
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddModalOpen(true)}>
              添加用户
            </Button>
          </Space>
        )}
      </div>

      <Card className="content-card">
      <Tabs
        items={[
          {
            key: 'users',
            label: `全部用户 (${users.length})`,
            children: (
              <Table
                rowKey="id"
                columns={userColumns}
                dataSource={users}
                loading={loading}
                pagination={{ pageSize: 10 }}
              />
            ),
          },
          {
            key: 'pending',
            label: `待审批 (${pendingUsers.length})`,
            children: (
              <Table
                rowKey="id"
                columns={pendingColumns}
                dataSource={pendingUsers}
                loading={pendingLoading}
                pagination={{ pageSize: 10 }}
              />
            ),
          },
          {
            key: 'whitelist',
            label: `白名单分组 (${whitelistTotal})`,
            children: (
              <Table
                rowKey="department"
                columns={whitelistGroupColumns}
                dataSource={whitelistGroups}
                loading={whitelistLoading}
                pagination={{ pageSize: 10 }}
                expandable={{
                  expandedRowRender: (record) => (
                    <Table
                      rowKey={(item) => `${item.id}-${item.email}`}
                      columns={whitelistUserColumns}
                      dataSource={record.users}
                      pagination={false}
                      size="small"
                    />
                  ),
                }}
              />
            ),
          },
        ]}
      />
      </Card>

      <Modal
        title="添加用户"
        open={addModalOpen}
        onCancel={() => {
          setAddModalOpen(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        confirmLoading={addLoading}
        maskClosable={false}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleAddUser}
          autoComplete="off"
        >
          <Form.Item
            label="姓名"
            name="name"
            rules={[{ required: true, message: '请输入姓名' }]}
          >
            <Input placeholder="请输入姓名" />
          </Form.Item>
          <Form.Item
            label="邮箱"
            name="email"
            rules={[
              { required: true, message: '请输入邮箱' },
              { type: 'email', message: '请输入有效的邮箱地址' },
              {
                validator: (_, value) => {
                  if (!value || value.toLowerCase().endsWith('@welzek.com')) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('仅支持 @welzek.com 邮箱'));
                },
              },
            ]}
          >
            <Input placeholder="name@welzek.com" />
          </Form.Item>
          <Form.Item
            label="密码"
            name="password"
            rules={[
              { required: true, message: '请输入密码' },
              { min: 6, message: '密码长度至少6位' },
            ]}
          >
            <Input.Password placeholder="请设置初始密码" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
